import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { VehiclesService } from 'src/app/_services/vehicles.service';
import { Vehicle } from 'src/app/_models/vehicle';
import { BehaviorSubject, Subscription } from 'rxjs';
import { StatisticsService } from 'src/app/_services/statistics.service';
import { MostRecentChangesService } from 'src/app/_services/most-recent-changes.service';
import { ActiveIssues } from 'src/app/_models/active-issues';
import { IssuesService } from 'src/app/_services/issues.service';
import { ServiceReminderService } from 'src/app/_services/service-reminder.service';
import { ServiceReminders } from 'src/app/_models/service-reminders';
import { AdminService } from 'src/app/_services/admin.service';

export interface PeriodicElement {
  name: string;
  position: number;
  weight: number;
  symbol: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {

  activeIssues: any[] = [];
  activeIssuesTotal = 0;
  activeIssuesLoading = true;

  vehicleCount = 0;

  serviceReminders: ServiceReminders[] = [];
  serviceRemindersLoading = true;

  // Predictive maintenance — rule-based risk signals, with an AI narrative
  // when an AI provider is configured in Admin. Cached server-side (daily
  // worker job), so this read is cheap.
  predictions: any[] = [];
  predictionsLoading = true;

  statisticsVehiclesQuery$: Subscription | undefined;

  statisticsVehicles$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  loadingVehicles$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  statisticsActiveIssuesQuery$: Subscription | undefined;
  statisticsActiveIssues$: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  loadingActiveIssues$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  mostRecentChangesActiveIssuesQuery$: Subscription | undefined;
  mostRecentChangesActiveIssues$: BehaviorSubject<ActiveIssues[]> =
    new BehaviorSubject<ActiveIssues[]>([]);

  vehicles: Vehicle[] = [];
  vehicles$: BehaviorSubject<Vehicle[]> = new BehaviorSubject<Vehicle[]>([]);

  vehicleStatusLegend = [
    { label: 'Active', percentage: '76%', value: 62, color: '#55b578' },
    {
      label: 'In maintenance',
      percentage: '15%',
      value: 12,
      color: '#f4ad37',
    },
    { label: 'Out of service', percentage: '6%', value: 5, color: '#df4e49' },
    { label: 'Idle', percentage: '4%', value: 3, color: '#3d72ef' },
  ];

  technicalRecordsLegend = [
    {
      label: 'General inspection',
      percentage: '43%',
      value: 48,
      color: '#3d72ef',
    },
    { label: 'Brakes', percentage: '20%', value: 22, color: '#df4e49' },
    { label: 'Engine', percentage: '16%', value: 18, color: '#f4ad37' },
    { label: 'Electrical', percentage: '13%', value: 14, color: '#7d4bdd' },
    { label: 'Hydraulic', percentage: '8%', value: 9, color: '#4aa3df' },
  ];

  vehicleStatusTotal = this.vehicleStatusLegend.reduce(
    (total, item) => total + item.value,
    0
  );

  technicalRecordsTotal = this.technicalRecordsLegend.reduce(
    (total, item) => total + item.value,
    0
  );

  vehicleStatusChartData = {
    labels: this.vehicleStatusLegend.map((item) => item.label),
    datasets: [
      {
        data: this.vehicleStatusLegend.map((item) => item.value),
        backgroundColor: this.vehicleStatusLegend.map((item) => item.color),
        hoverBackgroundColor: this.vehicleStatusLegend.map(
          (item) => item.color
        ),
        borderColor: '#ffffff',
        borderWidth: 4,
      },
    ],
  };

  technicalRecordsChartData = {
    labels: this.technicalRecordsLegend.map((item) => item.label),
    datasets: [
      {
        data: this.technicalRecordsLegend.map((item) => item.value),
        backgroundColor: this.technicalRecordsLegend.map((item) => item.color),
        hoverBackgroundColor: this.technicalRecordsLegend.map(
          (item) => item.color
        ),
        borderColor: '#ffffff',
        borderWidth: 4,
      },
    ],
  };

  doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  constructor(
    public mostRecentChangesService: MostRecentChangesService,
    public vehiclesService: VehiclesService,
    public statisticsService: StatisticsService,
    public issuesService: IssuesService,
    private serviceReminderService: ServiceReminderService,
    private adminService: AdminService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.vehiclesService.getAll();
    this.getActiveIssueVehicleCount();

    this.statisticsVehiclesQuery$ = this.statisticsService
      .getVehicleStatistics()
      .subscribe((res: any) => {
        this.statisticsVehicles$.next(res);
        this.loadingVehicles$.next(false);
      });

    this.statisticsActiveIssuesQuery$ = this.statisticsService
      .getActiveIssueStatistics()
      .subscribe((res: any) => {
        this.statisticsActiveIssues$.next(res);
        this.loadingActiveIssues$.next(false);
      });

    this.mostRecentChangesActiveIssuesQuery$ = this.mostRecentChangesService
      .getMostRecentChangesActiveIssues()
      .subscribe((res: any) => {
        // console.log(res);
        this.mostRecentChangesActiveIssues$.next(res);
      });

      this.issuesService.getActiveIssues().subscribe((res: any) => {
        const records: any[] = res.body.records ?? [];
        this.activeIssues = records.sort((a, b) => {
          const tA = a.date_updated ? new Date(a.date_updated).getTime() : 0;
          const tB = b.date_updated ? new Date(b.date_updated).getTime() : 0;
          return tB - tA;
        });
        this.activeIssuesTotal = res.body.total_active_issues;
        this.activeIssuesLoading = false;
      });

      this.serviceReminderService.getReminders(1, 50).subscribe((res: any) => {
        const reminders: ServiceReminders[] = res.body?.service_reminders ?? [];
        this.serviceReminders = reminders.sort((a, b) => {
          const urgA = this.urgencyRank(a);
          const urgB = this.urgencyRank(b);
          if (urgA !== urgB) return urgA - urgB;
          return (a.service_delta ?? Infinity) - (b.service_delta ?? Infinity);
        });
        this.serviceRemindersLoading = false;
      });

      this.vehiclesService.getVehicles().subscribe((vehicles: Vehicle[]) => {
        this.vehicles$.next(vehicles);
      });

      // Most urgent vehicles only — score 80 and up — so the card stays a
      // short, actionable shortlist rather than a wall of amber.
      this.adminService.getPredictions('high').subscribe({
        next: (res: any[]) => {
          this.predictions = (res ?? []).filter((p) => p.risk_score >= 80);
          this.predictionsLoading = false;
        },
        error: () => {
          this.predictions = [];
          this.predictionsLoading = false;
        },
      });

  }

  predictionSignal(p: any, key: string): any {
    return p?.signals ? p.signals[key] : null;
  }

  // One short line: the two most urgent rule-based flags. Full detail lives on
  // the vehicle's records page (a click away).
  predictSummary(p: any): string {
    const issues = (p?.signals?.likely_issues as string[]) ?? [];
    return issues.slice(0, 2).join(' · ');
  }

  // Open the vehicle's technical records, same as selecting it on the
  // Vehicles page: stash the full vehicle (matched from the loaded list, or a
  // minimal stand-in) and navigate to /technical, which reads selectedVehicle.
  openVehicle(p: any): void {
    const match = this.vehicles$.value?.find(
      (v) => v.vehicle_reference === p.vehicle_reference
    );
    const vehicle = match ?? {
      id: p.vehicle_id,
      vehicle_reference: p.vehicle_reference,
    };
    this.vehiclesService.selectedVehicle = vehicle;
    localStorage.setItem('selectedVehicle', JSON.stringify(vehicle));
    this.router.navigate(['/technical']);
  }

  trackByPrediction(index: number, p: any): number | string {
    return p?.vehicle_id ?? p?.vehicle_reference ?? index;
  }

  private urgencyRank(r: ServiceReminders): number {
    const kmLevel = this.kmUrgency(r.service_delta);
    const dateLevel = this.dateUrgency(r.service_due_date);
    return Math.min(kmLevel, dateLevel);
  }

  private kmUrgency(km: number): number {
    if (km <= 1000) return 0; // critical / red
    if (km <= 3000) return 1; // warning / yellow
    return 2;                 // ok / green
  }

  private dateUrgency(dueDate: any): number {
    if (!dueDate) return 2;
    const diff = Math.ceil(
      (new Date(dueDate).getTime() - Date.now()) / 86_400_000
    );
    if (diff < 0) return 0;
    if (diff <= 7) return 1;
    return 2;
  }

  getReminderUrgency(r: ServiceReminders): 'critical' | 'warning' | 'ok' {
    const rank = this.urgencyRank(r);
    if (rank === 0) return 'critical';
    if (rank === 1) return 'warning';
    return 'ok';
  }

  getReminderProgress(r: ServiceReminders): number {
    const vehicle = this.vehicles$.value?.find(
      (v) => v.vehicle_reference === r.vehicle_reference
    );

    const interval = vehicle?.service_interval;
    if (!interval || interval <= 0 || r.service_delta == null) return 0;
    const fraction = r.service_delta / interval;
    if (fraction < 0 || fraction > 1) return 0;
    return Math.round(fraction * 100);
  }

  trackByReminder(index: number, reminder: ServiceReminders): number | string {
    return reminder.id ?? reminder.vehicle_reference ?? index;
  }

  getActiveIssueVehicleCount(): number {
    this.issuesService.getActiveIssueVehicleCount().subscribe((res: any) => {
      console.log('Active issue vehicle count:', res);
      
      this.vehicleCount = res.body.vehicle_count;
    });
    return this.vehicleCount;
  } 


  ngOnDestroy(): void {
    if (this.statisticsVehiclesQuery$) {
      this.statisticsVehiclesQuery$.unsubscribe();
    }
    if (this.statisticsActiveIssuesQuery$) {
      this.statisticsActiveIssuesQuery$.unsubscribe();
    }

    if (this.mostRecentChangesActiveIssuesQuery$) {
      this.mostRecentChangesActiveIssuesQuery$.unsubscribe();
    }
  }
}
